import React from 'react';

import {CUSTOM_ICON_LOADERS} from './custom-icon-registry';
import type {CustomIconComponent, CustomIconName, CustomIconProps} from './custom-icon.type';

type CustomIconWithNameProps = CustomIconProps & {
    /**
     * The name of the custom SVG icon to render.
     */
    name: CustomIconName;
};

// Cache the React.lazy wrapper per icon so the same lazy component (and its already-resolved
// chunk) is reused across renders instead of being recreated on every mount.
const lazyComponentCache = new Map<CustomIconName, React.LazyExoticComponent<CustomIconComponent>>();

const getLazyCustomIcon = (name: CustomIconName): React.LazyExoticComponent<CustomIconComponent> => {
    const cached = lazyComponentCache.get(name);
    if (cached) {
        return cached;
    }

    // Pin the loader's type so React.lazy infers CustomIconComponent. Without this, an empty
    // registry makes `name` resolve to `never`, React.lazy can't infer its generic, and it falls
    // back to ComponentType<any> — which isn't assignable to LazyExoticComponent<CustomIconComponent>.
    const loadIcon: () => Promise<{default: CustomIconComponent}> = CUSTOM_ICON_LOADERS[name];
    const lazyComponent = React.lazy(loadIcon);
    lazyComponentCache.set(name, lazyComponent);
    return lazyComponent;
};

/**
 * Renders a custom, hand-authored SVG icon by name. The SVG is code-split and loaded lazily; a
 * same-sized placeholder is shown while its chunk is being fetched to avoid layout shift.
 *
 * It forwards the same style inputs a regular font icon gets (`size`, `color`, `filled`, and the
 * full `sx`), leaving each SVG free to decide which parts to use.
 *
 * @example
 * // once a custom icon is registered in `CustomIconName` / `custom-icon-registry.ts`:
 * <CustomIcon name="my-icon" size={24} color="currentColor" />
 */
export const CustomIcon = ({name, size, color, filled, sx}: CustomIconWithNameProps) => {
    const LazyIcon = getLazyCustomIcon(name);

    return (
        <React.Suspense fallback={<svg width={size} height={size} aria-hidden="true" />}>
            <LazyIcon size={size} color={color} filled={filled} sx={sx} />
        </React.Suspense>
    );
};

CustomIcon.displayName = 'CustomIcon';
